/**
 * Admin Dashboard Component - Detachable Admin Module
 * 
 * Direct user management interface without extra navigation.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import QuickActionsModal from './QuickActionsModal';
import BulkActionsModal from './BulkActionsModal';
import { useAdminData } from '../hooks/useAdminData';
import { 
  formatCredits, 
  getPlanInfo, 
  formatUserStatus, 
  getUserDisplayName, 
  getUserInitials,
  debounce 
} from '../utils';
import type { PlatformUser, UserFilters } from '../types';

/**
 * Main admin dashboard with integrated user management
 */
export const AdminDashboard: React.FC = () => {
  const { users, operations } = useAdminData();
  
  // UI state
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    enabled: undefined,
    planType: undefined
  });
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  
  // Quick actions state
  const [quickAction, setQuickAction] = useState<{
    user: PlatformUser | null;
    action: 'addCredits' | 'changePlan' | null;
  }>({ user: null, action: null });
  
  // Bulk actions state
  const [bulkAction, setBulkAction] = useState<'addCredits' | 'changePlan' | null>(null);
  

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    users.searchUsers(query);
  }, 300);

  useEffect(() => {
    users.loadUsers();
  }, []);

  useEffect(() => {
    if (filters.search) {
      debouncedSearch(filters.search);
    }
  }, [filters.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allUserIds = new Set(filteredUsers.map(user => user.id || user.email));
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleQuickAction = (user: PlatformUser, action: 'addCredits' | 'changePlan') => {
    setQuickAction({ user, action });
  };

  const handleQuickActionClose = () => {
    setQuickAction({ user: null, action: null });
  };

  const handleQuickActionSuccess = () => {
    // Refresh user list to show updated data
    users.refreshUsers();
  };

  const handleBulkAction = (action: 'addCredits' | 'changePlan') => {
    setBulkAction(action);
  };

  const handleBulkActionClose = () => {
    setBulkAction(null);
  };

  const handleBulkActionSuccess = () => {
    // Refresh user list and clear selection
    users.refreshUsers();
    setSelectedUsers(new Set());
  };

  // Filter and sort users based on current filters
  const filteredUsers = (users.users || [])
    .filter(user => {
      if (filters.enabled !== undefined && user.enabled !== filters.enabled) {
        return false;
      }
      if (filters.planType && user.billing.plan_type !== filters.planType) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      // Sort by creation date (most recent first)
      const timestampA = a.createdTimestamp || 0;
      const timestampB = b.createdTimestamp || 0;
      return timestampB - timestampA;
    });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Search Bar */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search users by email, name, or username..."
                value={filters.search}
                onChange={handleSearchChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 text-sm font-medium rounded border transition-colors ${
                showFilters 
                  ? 'bg-blue-50 text-blue-700 border-blue-200' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Status
                </label>
                <select
                  value={filters.enabled === undefined ? '' : filters.enabled.toString()}
                  onChange={(e) => handleFilterChange('enabled', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Type
                </label>
                <select
                  value={filters.planType || ''}
                  onChange={(e) => handleFilterChange('planType', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Plans</option>
                  <option value="starter">Starter</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="unlimited">Unlimited</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedUsers.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedUsers.size} users selected
              </span>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleBulkAction('addCredits')}
                  className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                >
                  Add Credits
                </button>
                
                <button
                  onClick={() => handleBulkAction('changePlan')}
                  className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded hover:bg-purple-200"
                >
                  Change Plan
                </button>
                
                <button
                  onClick={() => setSelectedUsers(new Set())}
                  className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {users.loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        )}

        {/* Error State */}
        {users.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">⚠️</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Users</h3>
                <p className="mt-1 text-sm text-red-700">{users.error}</p>
                <button
                  onClick={() => users.refreshUsers()}
                  className="mt-2 text-sm text-red-600 hover:text-red-500 font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        {!users.loading && !users.error && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 table-fixed" style={{ minWidth: '900px' }}>
                <colgroup>
                  <col className="w-10" />
                  <col />
                  <col className="w-[100px]" />
                  <col className="w-[100px]" />
                  <col className="w-[90px]" />
                  <col className="w-[90px]" />
                  <col className="w-[100px]" />
                  <col className="w-[220px]" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Life Time Usage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id || user.email} className="hover:bg-gray-50">
                      <td className="px-2 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id || user.email)}
                          onChange={(e) => handleSelectUser(user.id || user.email, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      
                      <td className="px-4 py-4 overflow-hidden">
                        <div className="flex items-center min-w-0">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
                              {getUserInitials(user)}
                            </div>
                          </div>
                          <div className="ml-3 min-w-0 overflow-hidden">
                            <div className="text-sm font-medium text-gray-900 truncate" title={getUserDisplayName(user)}>
                              {getUserDisplayName(user)}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={user.email}>{user.email}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-4 py-4">
                        {(() => {
                          const status = formatUserStatus(user);
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${status.color}`}>
                              {status.text}
                            </span>
                          );
                        })()}
                      </td>
                      
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                          getPlanInfo(user.billing.plan_type).color
                        }`}>
                          {getPlanInfo(user.billing.plan_type).label}
                        </span>
                      </td>
                      
                      <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatCredits(user.billing.total_credits)}
                      </td>

                      <td className="px-2 py-4 text-sm text-gray-900 whitespace-nowrap">
                        {formatCredits(user.billing.total_lifetime_usage)}
                      </td>

                      <td className="px-4 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {user.createdTimestamp 
                          ? (() => {
                              const date = new Date(user.createdTimestamp);
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              const year = date.getFullYear();
                              return `${month}/${day}/${year}`;
                            })()
                          : 'N/A'}
                      </td>
                      
                      <td className="px-4 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <Link
                            to={`/admin/users/${encodeURIComponent(user.id || user.email)}`}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            View Details
                          </Link>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handleQuickAction(user, 'addCredits')}
                            className="text-sm text-green-600 hover:text-green-700 font-medium"
                          >
                            Add Credits
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handleQuickAction(user, 'changePlan')}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Change Plan
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {filteredUsers.length === 0 && !users.loading && (
              <div className="text-center py-12">
                <p className="text-gray-500">No users found matching your criteria</p>
                {(filters.search || filters.enabled !== undefined || filters.planType) && (
                  <button
                    onClick={() => setFilters({ search: '', enabled: undefined, planType: undefined })}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        )}


        {/* Quick Actions Modal */}
        <QuickActionsModal
          user={quickAction.user}
          action={quickAction.action}
          onClose={handleQuickActionClose}
          onSuccess={handleQuickActionSuccess}
        />

        {/* Bulk Actions Modal */}
        <BulkActionsModal
          users={filteredUsers.filter(user => selectedUsers.has(user.id || user.email))}
          action={bulkAction}
          onClose={handleBulkActionClose}
          onSuccess={handleBulkActionSuccess}
        />

      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;